import React, { useState, useEffect } from 'react';
import supabase from '../../../../supabase-client';
import Icon from '../../../components/AppIcon';
import ShareButton from '../../../components/ui/ShareButton';

const BookInfo = ({ book }) => {
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    const fetchReviewCount = async () => {
      const { count, error } = await supabase
        .from('userbook')
        .select('id_userbook', { count: 'exact', head: true })
        .eq('id_product', book?.id);

      if (!error && typeof count === 'number') {
        setReviewCount(count);
      } else {
        console.error('Error al contar reseñas:', error);
      }
    };

    if (book?.id) {
      fetchReviewCount();
    }
  }, [book?.id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const renderStars = (rating = 0) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="StarHalf" size={16} className="text-yellow-400 fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={16} className="text-gray-300" />
      );
    }

    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Título y autor */}
      <div>
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
          {book?.title}
        </h1>
        <p className="text-sm sm:text-lg text-muted-foreground">
          por <span className="font-medium text-foreground">{book?.author || book?.authorName || 'Autor no especificado'}</span>
        </p>
      </div>

      {/* Calificación y reseñas */}
      <div className="flex items-center space-x-4">
        {book?.rating && book.rating > 0 ? (
          <>
            <div className="flex items-center space-x-1">
              {renderStars(book.rating)}
            </div>
            <span className="text-sm font-medium text-foreground">
              {book.rating.toFixed(1)}
            </span>
          </>
        ) : (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              {renderStars(0)}
            </div>
            <span className="text-sm text-muted-foreground italic">
              Sin calificación
            </span>
          </div>
        )}
      </div>

      {/* Precio y Compartir */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xl sm:text-3xl font-bold text-accent">
            {formatPrice(book?.originalPrice || book?.price)}
          </span>
          <ShareButton
            title={book?.title}
            description={`Libro por ${book?.author || book?.authorName || 'Autor no especificado'}`}
            price={book?.originalPrice || book?.price}
            productType="libro"
          />
        </div>
      </div>

      {/* Información rápida */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-muted rounded-lg lg:max-w-lg">
        <InfoItem label="Páginas" value={book?.pages} />
        <InfoItem label="Idioma" value={book?.language} />
        {book?.publishDate && <InfoItem label="Fecha de publicación" value={book?.publishDate} />}
      </div>

      {/* Categorías */}
      {book?.categories?.length > 0 ? (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Categorías</h3>
          <div className="flex flex-wrap gap-2">
            {book.categories.map((category, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm hover:bg-secondary/80 cursor-pointer transition-smooth"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Categorías</h3>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm">
              Sin categoría
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <span className="text-sm text-muted-foreground block">{label}</span>
    <span className="font-medium">{value || 'No especificado'}</span>
  </div>
);

export default BookInfo;
